import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PanelLeft, Loader2, Menu, X } from 'lucide-react';

import { Sidebar } from '@/components/Sidebar';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { MeshFilesProvider } from '@/contexts/MeshFilesContext';
import { CreditsProvider } from '@/contexts/CreditsContext';
import { UnitProvider } from '@/contexts/UnitContext';
import { FloatingAuthModal } from '@/components/auth/FloatingAuthModal';
import { PostHogProvider } from '@/contexts/PostHogProvider';

export default function App() {
  const { isLoading } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    localStorage.getItem('sidebarOpen') !== 'false',
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarOpen', isSidebarOpen.toString());
  }, [isSidebarOpen]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-adam-bg-secondary-dark">
        <Loader2 className="h-8 w-8 animate-spin text-adam-blue" />
      </div>
    );
  }

  return (
    <PostHogProvider>
      <UnitProvider>
        <CreditsProvider>
          <div className="h-dvh overflow-hidden">
            {isMobile ? (
              /* Mobile Layout */
              <div className="flex h-dvh flex-col">
                {/* Mobile Header with Hamburger */}
                <div className="flex h-14 shrink-0 items-center justify-between border-b border-dc-border-secondary bg-dc-bg-primary px-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-dc-text-primary"
                    onClick={() => setIsMobileSidebarOpen(true)}
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                  <div className="flex items-center gap-2">
                    <img
                      src={`${import.meta.env.BASE_URL}/daddycad-logo.png`}
                      alt="Logo"
                      className="h-6 w-6"
                    />
                    <span className="text-lg font-bold text-dc-text-primary">DaddyCad</span>
                  </div>
                  <div className="w-9" /> {/* Spacer for centering */}
                </div>

                {/* Mobile Sidebar Overlay */}
                {isMobileSidebarOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                      onClick={() => setIsMobileSidebarOpen(false)}
                    />
                    {/* Sidebar Drawer */}
                    <div className="fixed inset-y-0 left-0 z-50 w-72 animate-in slide-in-from-left duration-300">
                      <div className="relative h-full">
                        <Sidebar isSidebarOpen={true} />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-2 top-3 h-8 w-8 text-dc-text-tertiary hover:text-dc-text-primary"
                          onClick={() => setIsMobileSidebarOpen(false)}
                        >
                          <X className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                {/* Main Content */}
                <div className="flex-1 overflow-auto bg-adam-bg-dark">
                  <MeshFilesProvider>
                    <Outlet context={{ isSidebarOpen: false, isMobile: true }} />
                  </MeshFilesProvider>
                </div>

                {/* Floating auth modal for unauthenticated users */}
                <FloatingAuthModal />
              </div>
            ) : (
              /* Desktop Layout */
              <div className="flex h-dvh transition-all ease-in-out">
                <Sidebar isSidebarOpen={isSidebarOpen} />
                <div className="relative flex-1 overflow-auto bg-adam-bg-dark">
                  {/* Toggle Sidebar Button - Positioned on main content area */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`bg-adam-neutral-3000 fixed z-10 h-7 w-7 rounded-md text-gray-400 transition-all duration-300 [@media(hover:hover)]:hover:bg-adam-neutral-950 [@media(hover:hover)]:hover:text-adam-neutral-10 ${isSidebarOpen ? 'left-[272px]' : 'left-20'
                      } ${location.pathname === '/' && isSidebarOpen
                        ? 'top-[2.25rem]'
                        : 'top-3.5'
                      }`}
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  >
                    <PanelLeft className="h-4 w-4" />
                  </Button>
                  <div className="h-full bg-adam-bg-dark">
                    <MeshFilesProvider>
                      <Outlet context={{ isSidebarOpen, isMobile: false }} />
                    </MeshFilesProvider>
                  </div>
                </div>
                {/* Floating auth modal for unauthenticated users - outside overflow container */}
                <FloatingAuthModal />
              </div>
            )}
          </div>
        </CreditsProvider>
      </UnitProvider>
    </PostHogProvider>
  );
}

